import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination,
  Search
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { DropdownLink, DropdownSelectable } from 'components/common/dropdown';
import {
  CompetenciesAdd,
  CompetencyCopy,
  CompetenciesListItem,
  CompetenciesRemove,
  CompetenciesSettingsButton
} from '.';

import type { GetCompetenciesRes } from 'shared-types/responses';

const Competencies = () => {
  const {
    messages,
    serverConfig: { competenciesAllowAdmins },
    user
  } = useReduxState();

  const router = useRouter();
  const page = router.query.page ? Number(router.query.page) : 1;
  const category = (router.query.category as string) || 'all';
  const search = (router.query.search as string) || '';

  const { data, loading, updateData } = useApiData<GetCompetenciesRes>({
    url: `/admin/competencies?category=${category}&search=${search}&page=${page}`
  });

  const isSuperadmin = user?.roles?.includes('superadmin');

  const handlePageChange = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } }, undefined, {
      shallow: true
    });
  };

  const handleCategoryChange = (value: string) => {
    const newQuery: Record<string, string> = { ...router.query, page: '1' };
    if (value !== 'all') newQuery.category = value;
    else delete newQuery.category;

    router.replace({ query: newQuery }, undefined, { shallow: true });
  };

  return (
    <div className="competencies">
      <Box
        headerContent={
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            <DropdownSelectable
              category="lighter"
              defaultValue="all"
              onChange={handleCategoryChange}
              options={[
                { text: messages.commonAll, value: 'all' },
                ...(data?.categories.map((category) => ({
                  text: category,
                  value: category
                })) || [])
              ]}
              selectedValueBefore={`${messages.commonCategory}:`}
              showSelectedText={true}
              size="header"
              value={category}
            />

            <div style={{ minWidth: 200 }}>
              <Search
                placeholder={messages.commonSearch}
                withUrl={true}
                size="header"
              />
            </div>

            <CompetenciesAdd />
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.commonUserCompetencies}
        titleDescription={
          isSuperadmin ? (
            <FlexContainer style={{ gap: 10, flexWrap: 'wrap' }}>
              <div>
                {competenciesAllowAdmins
                  ? messages.commonManagedByAdminsAndSuperadmins
                  : messages.commonManagedBySuperadminsOnly}
              </div>

              <CompetenciesSettingsButton />
            </FlexContainer>
          ) : null
        }
      >
        {!data?.competencyObjs?.length ? (
          <EmptyIndicator icon="medal" title={messages.commonNoCompetencies} />
        ) : (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonCompetence}
              </BoxTableCell>

              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonCategories}
              </BoxTableCell>

              <BoxTableCell cellBreakpoint={breakpoints.medium} shrink={true} />
            </BoxTableHeader>

            {data.competencyObjs.map((competency) => (
              <BoxTableRow key={competency.id}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <CompetenciesListItem competency={competency} />
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonCategories}
                  verticalAlign="top"
                >
                  {competency.categories?.length ? (
                    <div className="categories" style={{ paddingTop: 5 }}>
                      {competency.categories.join(', ')}
                    </div>
                  ) : null}
                </BoxTableCell>

                <BoxTableCell verticalAlign="top">
                  <MoreOptions styleContentButtons={false}>
                    <DropdownLink
                      icon="pen"
                      href={`/users/competencies/${competency.id}`}
                      text={messages.competenciesEdit}
                    />

                    <CompetencyCopy
                      competency={competency}
                      updateCompetencies={updateData}
                    />

                    <CompetenciesRemove
                      competencyId={competency.id}
                      onRemove={updateData}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Competencies;
